import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Package } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog'

// Servicios
import { taxesService } from '../services/taxes.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
// Define aquí cómo es el objeto que viene del Backend
interface TaxItem {
    id: number
    name: string
    percentage: number
    type: string
}

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
// Aquí configuras si un campo es obligatorio, cuántas letras mínimas, correos, etc.
const formSchema = z.object({
    name: z.string().min(1, 'El nombre es obligatorio').min(3, 'El nombre debe tener al menos 3 caracteres'),
    percentage: z.coerce.number().min(0, 'El porcentaje debe ser mayor o igual a 0'),
    type: z.string().min(1, 'El tipo es obligatorio').min(3, 'El tipo debe tener al menos 3 caracteres'),
})

// Extrae el tipo de dato para Typescript a partir del esquema Zod
type FormValidate = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function Taxes() {
    // ─── Estados (Variables Reactivas) ───
    const [data, setData] = useState<TaxItem[]>([])
    const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [refreshCount, setRefreshCount] = useState(0) // Usado para forzar que la tabla se recargue

    // Control de Modales
    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

    // Paginación
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10

    // ─── Formulario (React Hook Form) ───
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<FormValidate>({
        mode: 'onChange',
        resolver: zodResolver(formSchema as any),
        defaultValues: { name: '', percentage: 0, type: '' }
    })

    // ─── Efecto: Cargar Datos de la API ───
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const response = await taxesService.getItems({
                    search: searchQuery,
                    page: currentPage
                })

                // Ajustar según cómo tu backend devuelva el JSON:
                const items = response.data?.data || response.data || []
                setData(items)

                // Guardar metadatos de paginación del backend (Laravel)
                setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
            } catch (error) {
                toast.error('Error al cargar los datos')
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [refreshCount, searchQuery, currentPage]) // Al cambiar estas dependencias, este bloque vuelve a ejecutarse

    // Los datos ya vienen filtrados y paginados del backend
    const paginatedData = data

    // ─── Funciones de Interfaz ───
    const openCreateModal = () => {
        setEditingId(null)
        reset({ name: '', percentage: 0, type: '' }) // Limpia el formulario
        setShowModal(true)
    }

    const openEditModal = (item: TaxItem) => {
        setEditingId(item.id)
        reset({ name: item.name, percentage: item.percentage, type: item.type }) // Carga datos viejos
        setShowModal(true)
    }

    // ─── Funciones API (Guardar y Eliminar) ───
    const onSubmit = async (formData: FormValidate) => {
        try {
            if (editingId) {
                // Editar
                await taxesService.updateItem(editingId, formData)
                toast.success('Registro actualizado exitosamente')
            } else {
                // Crear
                await taxesService.createItem(formData)
                toast.success('Registro creado exitosamente')
            }
            setShowModal(false)
            setRefreshCount(prev => prev + 1) // Sumar 1 recarga la tabla automáticamente
        } catch (error) {
            toast.error('Error al guardar el registro')
        }
    }

    const executeDelete = async () => {
        if (!deleteConfirmId) return
        try {
            await taxesService.deleteItem(deleteConfirmId)
            toast.success('Registro eliminado')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al eliminar')
        } finally {
            setDeleteConfirmId(null)
        }
    }

    // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
    return (
        <div className="space-y-6 p-6">

            {/* 1. Header y Buscador */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Impuestos</h1>
                    <p className="mt-1 text-sm text-slate-600">Administra los impuestos del sistema.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative w-full sm:w-64">
                        <SearchFilter
                            placeholder="Buscar..."
                            onSearch={(text) => {
                                setSearchQuery(text)
                                setCurrentPage(1) // Regresar a la primera página al buscar
                            }}
                        />
                    </div>
                    <Button onClick={openCreateModal} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                        <Plus className="mr-2 h-4 w-4" /> Nuevo Impuesto
                    </Button>
                </div>
            </div>

            {/* 2. Tabla de Datos */}
            {isLoading ? (
                // Esqueleto: el número de 'columns' debe coincidir con tus columnas reales
                <TableSkeleton
                    headers={['Nombre', 'Porcentaje', 'Tipo', 'Acciones']}
                    columns={['text', 'text', 'text', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    {/* Encabezados de Tabla */}
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Porcentaje</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item) => (
                                        <tr key={item.id} className="hover:bg-slate-50">
                                            {/* Celdas de Datos */}
                                            <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.percentage}</td>
                                            <td className="px-6 py-4">
                                                {/* Etiqueta (Badge) de Estado */}
                                                <Badge className="bg-slate-100 text-slate-700">
                                                    {item.type}
                                                </Badge>

                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal(item)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                                                        <Edit className="h-4 w-4 text-slate-600" />
                                                    </button>
                                                    <button onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        {/* Estado de "No hay registros" */}
                                        <td colSpan={4} className="px-6 py-12 text-center">
                                            <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron registros</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 3. Paginación */}
                    {paginationMeta.total > 0 && (
                        <GlobalPaginator
                            totalItems={paginationMeta.total}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            from={paginationMeta.from}
                            to={paginationMeta.to}
                        />
                    )}
                </div>
            )}

            {/* 4. Modal Crear/Editar */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar Registro' : 'Nuevo Registro'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

                        {/* Input Nombre */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Nombre <span className="text-red-500">*</span></label>
                            {/* Con {...register('campo')} conectas el input con React Hook Form */}
                            <Input {...register('name')} placeholder="Ingresa el nombre" className={errors.name ? 'border-red-500 ' : ''} />
                            {/* Y aquí muestras el mensaje de error de Zod si ocurre */}
                            {errors.name && <FormError message={errors.name.message} />}
                        </div>

                        {/* Input Porcentaje */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Porcentaje (%) <span className="text-red-500">*</span></label>
                            <Input type="number" step="0.01" {...register('percentage')} placeholder="0.00%" className={errors.percentage ? 'border-red-500 focus-visible:ring-red-500' : ''} />
                            {errors.percentage && <FormError message={errors.percentage.message} />}
                        </div>

                        {/* Input Tipo */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Tipo <span className="text-red-500">*</span></label>
                            <Input {...register('type')} placeholder="Breve descripción" className={errors.type ? 'border-red-500 ' : ''} />
                            {errors.type && <FormError message={errors.type.message} />}
                        </div>

                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                                {/* Loader discreto que se muestra mientras se guarda */}
                                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* 5. Modal Global de Confirmación para Eliminar */}
            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="registro"
            />

        </div>
    )
}
